import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { czyMoznaPrzewinacWLewo, czyMoznaPrzewinacWPrawo } from '@/lib/scrollFade';
import { przesuniecieKolkiem, toPrzeciagniecie } from '@/lib/przewijanieMysza';

/**
 * Poziomo przewijany rząd z wygaszeniem przy prawej krawędzi.
 *
 * DLACZEGO ISTNIEJE: w wyszukiwarce są dwa takie rzędy jeden pod drugim — pasek obszarów
 * i rząd chipów filtrów. Oba przewijają się w poziomie i oba nie mówiły o tym ani słowa:
 * przy trzech obszarach drugi chip kończył się równo przy ramce, a trzeciego nie było
 * widać i nic nie sugerowało, że istnieje. Zgłosił to Piotr, patrząc na zrzut.
 *
 * Gradient pokazuje się TYLKO wtedy, gdy naprawdę jest co przewinąć, i znika po
 * doprzewinięciu do końca — inaczej byłby ozdobą, która kłamie w stanie końcowym.
 */
export function ScrollableRow({
  children,
  className,
  fadeFrom = 'white',
  zaokraglone = false,
  testId,
  mysz = false,
  refRzedu,
}: {
  children: ReactNode;
  /** Klasy samego przewijanego rzędu (układ, odstępy) — nie opakowania. */
  className?: string;
  /** Kolor, z którego gaśnie gradient — musi być tłem TEGO miejsca, nie ogólnym. */
  fadeFrom?: 'white' | 'background';
  /** Rząd ma zaokrągloną ramkę — gradient musi ją powtórzyć, bo prostokątny
   *  wystawałby rogami poza łuk. */
  zaokraglone?: boolean;
  testId?: string;
  /**
   * Pulpit (układ filtrów 6.10): kółko nad rzędem przewija go w bok, a wciśnięty przycisk myszy
   * go przeciąga (kursor „rączka”; ruch poniżej progu to wciąż kliknięcie). `src/lib/przewijanieMysza.ts`.
   */
  mysz?: boolean;
  /** Dostęp do przewijanego elementu (np. przewinięcie do nowo włączonego filtra). */
  refRzedu?: (el: HTMLDivElement | null) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [ciagnie, setCiagnie] = useState(false);
  const [maWiecej, setMaWiecej] = useState(false);
  const [maZLewej, setMaZLewej] = useState(false);

  const zmierz = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const wiecej = czyMoznaPrzewinacWPrawo(el);
    const zLewej = czyMoznaPrzewinacWLewo(el);
    setMaWiecej((poprzednie) => (poprzednie === wiecej ? poprzednie : wiecej));
    setMaZLewej((poprzednie) => (poprzednie === zLewej ? poprzednie : zLewej));
  }, []);

  // Bez tablicy zależności CELOWO. Dodanie chipa zmienia `scrollWidth`, ale nie zmienia
  // rozmiaru kontenera — więc ani ResizeObserver, ani nasłuch `resize` tego nie widzą.
  // Pomiar po każdym renderze kosztuje dwa odczyty właściwości i ustala się od razu, bo
  // stan zmienia się tylko przy faktycznej różnicy.
  useEffect(zmierz);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', zmierz, { passive: true });
    window.addEventListener('resize', zmierz);
    return () => {
      el.removeEventListener('scroll', zmierz);
      window.removeEventListener('resize', zmierz);
    };
  }, [zmierz]);

  useEffect(() => {
    refRzedu?.(ref.current);
    return () => refRzedu?.(null);
  }, [refRzedu]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !mysz) return;
    const naKolko = (e: WheelEvent) => {
      const nowe = przesuniecieKolkiem(e.deltaX, e.deltaY, el.scrollLeft, el.scrollWidth - el.clientWidth);
      if (nowe == null) return;
      e.preventDefault();
      el.scrollLeft = nowe;
    };
    let start: { x: number; scroll: number; id: number } | null = null;
    let przeciagniete = false;
    const naWcisniecie = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      start = { x: e.clientX, scroll: el.scrollLeft, id: e.pointerId };
      przeciagniete = false;
    };
    const naRuch = (e: PointerEvent) => {
      if (!start || e.pointerId !== start.id) return;
      const dx = e.clientX - start.x;
      if (!przeciagniete && !toPrzeciagniecie(dx)) return;
      if (!przeciagniete) {
        przeciagniete = true;
        setCiagnie(true);
        el.setPointerCapture?.(e.pointerId);
      }
      el.scrollLeft = start.scroll - dx;
    };
    const naPuszczenie = (e: PointerEvent) => {
      if (!start || e.pointerId !== start.id) return;
      start = null;
      setCiagnie(false);
      el.releasePointerCapture?.(e.pointerId);
    };
    // Kliknięcie kończące przeciągnięcie nie może nacisnąć chipa ani jego ×.
    const naKlik = (e: MouseEvent) => {
      if (!przeciagniete) return;
      przeciagniete = false;
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener('wheel', naKolko, { passive: false });
    el.addEventListener('pointerdown', naWcisniecie);
    el.addEventListener('pointermove', naRuch);
    el.addEventListener('pointerup', naPuszczenie);
    el.addEventListener('pointercancel', naPuszczenie);
    el.addEventListener('click', naKlik, true);
    return () => {
      el.removeEventListener('wheel', naKolko);
      el.removeEventListener('pointerdown', naWcisniecie);
      el.removeEventListener('pointermove', naRuch);
      el.removeEventListener('pointerup', naPuszczenie);
      el.removeEventListener('pointercancel', naPuszczenie);
      el.removeEventListener('click', naKlik, true);
    };
  }, [mysz]);

  return (
    <div className="relative">
      <div
        ref={ref}
        data-testid={testId}
        className={cn(
          'overflow-x-auto [&::-webkit-scrollbar]:hidden',
          mysz && (ciagnie ? 'cursor-grabbing select-none' : 'cursor-grab'),
          className,
        )}
        style={{ scrollbarWidth: 'none' }}
      >
        {children}
      </div>
      {maZLewej && (
        <div
          aria-hidden="true"
          data-testid="wygaszenie-z-lewej"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r to-transparent',
            fadeFrom === 'white' ? 'from-white' : 'from-background',
            zaokraglone && 'my-px ml-px rounded-l-2xl',
          )}
        />
      )}
      {maWiecej && (
        <div
          aria-hidden="true"
          data-testid="wygaszenie-z-prawej"
          className={cn(
            'pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l to-transparent',
            fadeFrom === 'white' ? 'from-white' : 'from-background',
            zaokraglone && 'my-px mr-px rounded-r-2xl',
          )}
        />
      )}
    </div>
  );
}

export default ScrollableRow;
